import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Could not load the human check'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/**
 * Cloudflare Turnstile "are you human" check for the signup form. `onToken`
 * gets the token when the check passes, and null when it expires or fails;
 * `onUnavailable` fires when the check can't load (for example, an ad blocker).
 */
export default function TurnstileWidget({ siteKey, onToken, onUnavailable }) {
  const container = useRef(null);
  const tokenCallback = useRef(onToken);
  tokenCallback.current = onToken;
  const unavailableCallback = useRef(onUnavailable);
  unavailableCallback.current = onUnavailable;

  useEffect(() => {
    if (!siteKey) return undefined;
    let widgetId = null;
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !container.current) return;
        widgetId = turnstile.render(container.current, {
          sitekey: siteKey,
          callback: (token) => tokenCallback.current(token),
          'expired-callback': () => tokenCallback.current(null),
          'error-callback': () => tokenCallback.current(null),
        });
      })
      .catch(() => {
        tokenCallback.current(null);
        unavailableCallback.current?.();
      });
    return () => {
      cancelled = true;
      if (widgetId !== null) window.turnstile?.remove(widgetId);
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={container} className="flex justify-center" />;
}
